import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { AceptacionesHeader } from './components/AceptacionesHeader';
import { AceptacionesTabs } from './components/AceptacionesTabs';
import { AceptacionesFilter } from './components/AceptacionesFilter';
import { AceptacionesTable } from './components/AceptacionesTable';
import type { AcceptanceRecord } from './data';

// --- Funciones Helpers ---

const getInitials = (fullName: string) => {
    if (!fullName) return 'U';
    const names = fullName.trim().split(' ');
    if (names.length >= 2) return (names[0][0] + names[1][0]).toUpperCase();
    return fullName.substring(0, 2).toUpperCase();
};

const getAvatarColor = (userId: number) => {
    const colors = ['bg-blue-500', 'bg-emerald-500', 'bg-indigo-500', 'bg-pink-500', 'bg-amber-500', 'bg-purple-500'];
    return colors[userId % colors.length];
};

const parseUserAgent = (ua: string) => {
    if (!ua) return 'Desconocido';
    let os = 'Desconocido';
    let browser = 'Navegador';

    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac OS')) os = 'Mac';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS') || ua.includes('iPhone')) os = 'iOS';

    if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Edge')) browser = 'Edge';

    return `${browser} / ${os}`;
};

// ------------------------------------------------------------------

export const AceptacionesPage = () => {
    // Estados de UI y Filtros
    const [activeTab, setActiveTab] = useState<'TERMS_CONDITIONS' | 'SERVICE_AGREEMENT'>('TERMS_CONDITIONS');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedVersion, setSelectedVersion] = useState('');
    const [selectedDate, setSelectedDate] = useState('');

    // Estados de API
    const [records, setRecords] = useState<AcceptanceRecord[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Al cambiar de pestaña limpia los filtros
    const handleTabChange = (tab: 'TERMS_CONDITIONS' | 'SERVICE_AGREEMENT') => {
        setActiveTab(tab);
        setSelectedVersion('');
        setSearchQuery('');
        setSelectedDate('');
    };

    useEffect(() => {
        const fetchAcceptances = async () => {
            setIsLoading(true);
            try {
                
                // Parámetros
                const params = new URLSearchParams();

                params.append('typeCode', activeTab);
                params.append('pageNumber', '1');
                params.append('pageSize', '20');

                if (searchQuery.trim()) {
                    params.append('search', searchQuery.trim());
                }

                if (selectedVersion) {
                    params.append('version', selectedVersion);
                }

                if (selectedDate) {
                    const [year, month, day] = selectedDate.split('-');
                    params.append('date', `${year}-${month}-${day}`);
                }

                // Servicio
                const endpoint = `Acceptances?${params.toString()}`;
                const response = await apiClient.get<any>(endpoint);

                let resBody = response;
                if (response.data && response.data.statusCode !== undefined) {
                    resBody = response.data;
                }

                //console.log("Respuesta procesada:", resBody);

                if (resBody.hasError) {
                    throw new Error(resBody.resultError?.message || "Error al cargar la lista");
                }

                const rawData = Array.isArray(resBody.data) ? resBody.data : [];

                // Mapear los datos
                const mappedData: AcceptanceRecord[] = rawData.map((item: any) => {
                    const dateObj = item.acceptedAt ? new Date(item.acceptedAt) : new Date();

                    return {
                        id: `#ACC-${item.userAcceptanceId || '000'}`,
                        userId: item.userId || 0,
                        userName: item.userFullName || 'Usuario Sin Nombre',
                        userInitials: getInitials(item.userFullName),
                        avatarColor: getAvatarColor(item.userId || 0),
                        acceptedAt: dateObj.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }),
                        time: dateObj.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
                        documentVersion: item.version || 'v.Desconocida',
                        ipAddress: item.ipAddress || 'Desconocida',
                        device: parseUserAgent(item.userAgent),
                        status: item.status === 'AC' ? 'vigente' : 'historico'
                    };
                });

                setRecords(mappedData);

            } catch (error) {
                console.error("Error fetching acceptances:", error);
                setRecords([]);
            } finally {
                setIsLoading(false);
            }
        };

        const timeoutId = setTimeout(() => {
            fetchAcceptances();
        }, 500);

        return () => clearTimeout(timeoutId);

    }, [activeTab, searchQuery, selectedVersion, selectedDate]);

    return (
        <div className="relative flex flex-col w-full h-screen overflow-hidden font-sans text-gray-900 bg-gray-50">
            <div className="flex flex-col flex-1 w-full h-full overflow-y-auto overflow-x-hidden hide-scrollbar bg-white">

                <AceptacionesHeader />

                <AceptacionesTabs
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                />

                <AceptacionesFilter
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    activeTab={activeTab}
                    selectedVersion={selectedVersion}
                    setSelectedVersion={setSelectedVersion}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                />

                <div className="flex-1 flex flex-col relative min-h-[400px]">
                    {isLoading && (
                        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/70 backdrop-blur-[1px]">
                            <Loader2 className="w-8 h-8 animate-spin text-[#4f6df5] mb-2" />
                            <span className="text-xs font-semibold text-gray-500">Cargando registros...</span>
                        </div>
                    )}

                    <AceptacionesTable records={records} />
                </div>

            </div>
        </div>
    );
};

export default AceptacionesPage;